import { createRouter, createWebHistory } from 'vue-router'
import { LoginView, ProjectsView, EditorView, AssetsView, RunChainView, RunListView, TurnVideosView, VersionsView, LocalizationView } from '../views'
import { ensureStudioProfile } from '../composables'

/**
 * @param {{ api: ReturnType<import('../api/client.js').createVisualGameStudioClient> }} opts
 */
export function createStudioRouter({ api }) {
  let verifiedAccessToken = ''
  let pendingSession = null

  const router = createRouter({
    history: createWebHistory(),
    routes: [
      { path: '/', redirect: '/projects' },
      { path: '/login', name: 'login', component: LoginView, meta: { public: true } },
      { path: '/projects', name: 'projects', component: ProjectsView },
      { path: '/packs/:packId/editor/:sectionId?', name: 'editor', component: EditorView, props: true },
      { path: '/packs/:packId/assets', name: 'assets', component: AssetsView, props: true },
      { path: '/packs/:packId/versions', name: 'versions', component: VersionsView, props: true },
      { path: '/packs/:packId/turn-videos', name: 'turn-videos', component: TurnVideosView, props: true },
      { path: '/packs/:packId/runs', name: 'run-list', component: RunListView, props: true },
      { path: '/packs/:packId/runs/:runId/chain', name: 'run-chain', component: RunChainView, props: true },
      { path: '/packs/:packId/localization', name: 'localization', component: LocalizationView, props: true },
      { path: '/:pathMatch(.*)*', redirect: '/projects' },
    ],
  })

  router.beforeEach(async (to) => {
    const sessionValid = await hasValidSession()
    if (to.meta.public) {
      if (to.name === 'login' && sessionValid) return { name: 'projects' }
      return true
    }
    if (sessionValid) return true
    return { name: 'login', query: { redirect: to.fullPath } }
  })

  async function hasValidSession() {
    let token = api.tokenStore.getAccessToken()
    if (!token) {
      if (!api.tokenStore.getRefreshToken()) {
        verifiedAccessToken = ''
        return false
      }
      try {
        token = await api.tokenStore.refresh()
      } catch {
        verifiedAccessToken = ''
        return false
      }
    }
    if (verifiedAccessToken === token) return true
    if (!pendingSession) {
      pendingSession = ensureStudioProfile(api)
        .then(() => {
          verifiedAccessToken = api.tokenStore.getAccessToken() || ''
          return true
        })
        .catch(() => {
          verifiedAccessToken = ''
          api.tokenStore.clear()
          return false
        })
        .finally(() => {
          pendingSession = null
        })
    }
    return pendingSession
  }

  return router
}
